import { axiosClient } from '../axiosClient';

// Cobranças pela AbacatePay (PIX copia e cola, boleto e link de cartão),
// geradas no painel. Rotas em /api/admin/cobrancas (área finance).

export type MetodoCobranca = 'PIX' | 'BOLETO' | 'CARTAO';
export type StatusCobranca =
  | 'PENDENTE'
  | 'PAGA'
  | 'EXPIRADA'
  | 'CANCELADA'
  | 'REEMBOLSADA'
  | 'FALHOU';
export type CicloCobranca = 'mensal' | 'anual';

export interface Cobranca {
  id: number;
  token_publico: string;
  /** Página pública onde o cliente paga (Pix, cartão ou boleto). */
  link_pagamento: string | null;
  provedor_id: string | null;
  /** Forma escolhida aqui ou a última usada pelo cliente. */
  metodo: MetodoCobranca | null;
  status: StatusCobranca;
  valor_centavos: number;
  descricao: string | null;
  plano_id: number | null;
  plano_nome: string | null;
  plano_codigo: string | null;
  ciclo: CicloCobranca | null;
  produtor_user_id: number | null;
  produtor_nome: string | null;
  cliente_nome: string | null;
  cliente_email: string | null;
  cliente_telefone: string | null;
  /** Sempre mascarado pela API. */
  cliente_documento: string | null;
  pix_copia_cola: string | null;
  /** Só vem no detalhe (GET /:id), não na lista. */
  pix_qrcode_base64?: string | null;
  boleto_linha_digitavel: string | null;
  boleto_codigo_barras: string | null;
  boleto_url: string | null;
  boleto_vencimento: string | null;
  cartao_bandeira: string | null;
  cartao_final: string | null;
  parcelas: number | null;
  parcelas_max: number | null;
  taxa_centavos: number | null;
  comprovante_url: string | null;
  expira_em: string | null;
  paga_em: string | null;
  reembolsada_em: string | null;
  modo_teste: boolean;
  created_at: string;
  /** Cobrança recorrente: fatura que esta cobrança paga. */
  fatura_id?: number | null;
  /** Baixa manual (recebido por fora): forma, observação e quem marcou. */
  baixa_manual_forma?: FormaBaixa | null;
  baixa_manual_observacao?: string | null;
  baixa_manual_manager_id?: number | null;
  /** O cliente pagou pela AbacatePay depois da baixa manual: reembolsar. */
  pagamento_duplicado_em?: string | null;
}

export type FormaBaixa = 'pix_direto' | 'dinheiro' | 'transferencia' | 'outro';

export interface MarcarPagaInput {
  forma: FormaBaixa;
  pagaEm?: string;
  observacao: string;
}

export interface ResumoStatus {
  status: StatusCobranca;
  quantidade: number;
  valorCentavos: number;
}

export interface ListaCobrancas {
  cobrancas: Cobranca[];
  total: number;
  page: number;
  limit: number;
  resumo: ResumoStatus[];
}

export interface PrecoPlano {
  planoCodigo: string;
  planoNome: string;
  ciclo: CicloCobranca;
  valorCentavos: number;
}

export interface NovaCobrancaInput {
  metodo: MetodoCobranca;
  valorCentavos?: number;
  planoCodigo?: string;
  ciclo?: CicloCobranca;
  produtorUserId?: number;
  descricao?: string;
  vencimento?: string;
  parcelasMax?: number;
  cliente?: { nome?: string; email?: string; telefone?: string; documento?: string };
}

export interface FiltrosCobranca {
  status?: StatusCobranca;
  metodo?: MetodoCobranca;
  produtorUserId?: number;
  busca?: string;
  page?: number;
  limit?: number;
}

export interface ConfigPagamentos {
  configurado: boolean;
  loja?: { nome: string; saldo: unknown } | null;
}

export const cobrancasApi = {
  config: () => axiosClient.get<ConfigPagamentos>('/admin/cobrancas/config').then((r) => r.data),
  precos: () =>
    axiosClient.get<{ precos: PrecoPlano[] }>('/admin/cobrancas/precos').then((r) => r.data.precos),
  listar: (filtros: FiltrosCobranca = {}) =>
    axiosClient.get<ListaCobrancas>('/admin/cobrancas', { params: filtros }).then((r) => r.data),
  obter: (id: number) =>
    axiosClient.get<{ cobranca: Cobranca }>(`/admin/cobrancas/${id}`).then((r) => r.data.cobranca),
  criar: (dados: NovaCobrancaInput) =>
    axiosClient
      .post<{ cobranca: Cobranca }>('/admin/cobrancas', dados)
      .then((r) => r.data.cobranca),
  atualizar: (id: number) =>
    axiosClient
      .post<{ cobranca: Cobranca }>(`/admin/cobrancas/${id}/atualizar`)
      .then((r) => r.data.cobranca),
  reembolsar: (id: number) =>
    axiosClient
      .post<{ cobranca: Cobranca }>(`/admin/cobrancas/${id}/reembolsar`)
      .then((r) => r.data.cobranca),
  marcarPaga: (id: number, dados: MarcarPagaInput) =>
    axiosClient
      .post<{ cobranca: Cobranca }>(`/admin/cobrancas/${id}/marcar-paga`, dados)
      .then((r) => r.data.cobranca),
  estornarBaixa: (id: number, observacao: string) =>
    axiosClient
      .post<{ cobranca: Cobranca }>(`/admin/cobrancas/${id}/estornar-baixa`, { observacao })
      .then((r) => r.data.cobranca),
};
